import React, { useEffect, useState } from 'react';
import { Cpu, RefreshCw, Loader2, Copy, ShieldCheck, ShieldOff, Search, Usb } from 'lucide-react';
import { API_CONFIG } from '../../config/api.config';

const API = API_CONFIG.python.apiUrl;
const tk = () => localStorage.getItem('jwt_token') || localStorage.getItem('auth_token') || localStorage.getItem('token') || '';

interface Device {
  id: number;
  mac: string;
  secure_id: string | null;
  device_name?: string;
  is_active: boolean;
  status: string;
  provisioned: boolean;
  teacher_email: string;
  teacher_name?: string;
  created_at?: string;
}

async function api(path: string, init?: RequestInit) {
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk()}`, ...(init?.headers || {}) },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `Lỗi ${res.status}`);
  }
  return res.json();
}

const LabDevices: React.FC = () => {
  const [rows, setRows] = useState<Device[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [copied, setCopied] = useState<string>('');

  const load = async () => {
    setLoading(true); setErr('');
    try { setRows(await api('/lab/admin/devices')); }
    catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const act = async (id: number, action: 'approve' | 'revoke' | 'reset-provision') => {
    setBusyId(id);
    try { await api(`/lab/admin/devices/${id}/${action}`, { method: 'POST' }); await load(); }
    catch (e: any) { alert(e.message); }
    finally { setBusyId(null); }
  };

  const copy = (s: string) => { navigator.clipboard?.writeText(s); setCopied(s); setTimeout(() => setCopied(''), 1200); };

  const filtered = rows.filter(d =>
    !q || d.mac.includes(q.toUpperCase()) || (d.teacher_email || '').toLowerCase().includes(q.toLowerCase())
  );
  const pending = rows.filter(d => !d.is_active).length;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><Usb className="text-blue-600" /> Thiết bị Lab (ESP32)</h1>
          <p className="text-sm text-gray-500 mt-1">Thiết bị giáo viên báo về khi nạp firmware qua web. Duyệt để cấp quyền theo kiến trúc Lab, rồi gửi mã Secure ID cho giáo viên.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium hover:bg-gray-50">
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Làm mới
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Tìm theo MAC hoặc email giáo viên…"
            className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none" />
        </div>
        <span className="text-xs font-bold text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{pending} chờ duyệt</span>
        <span className="text-xs font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">{rows.length} thiết bị</span>
      </div>

      {err && <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-xl p-3 text-sm mb-4">{err}</div>}

      <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse min-w-[860px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="p-4 text-[11px] font-black text-gray-500 uppercase">Giáo viên</th>
                <th className="p-4 text-[11px] font-black text-gray-500 uppercase">MAC</th>
                <th className="p-4 text-[11px] font-black text-gray-500 uppercase">Secure ID (32 ký tự)</th>
                <th className="p-4 text-[11px] font-black text-gray-500 uppercase">Trạng thái</th>
                <th className="p-4 text-[11px] font-black text-gray-500 uppercase text-right">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={5} className="p-10 text-center text-gray-400"><Loader2 className="animate-spin inline" /> Đang tải…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} className="p-10 text-center text-gray-400 text-sm">Chưa có thiết bị nào.</td></tr>
              ) : filtered.map(d => (
                <tr key={d.id} className="hover:bg-blue-50/30">
                  <td className="p-4">
                    <div className="font-semibold text-gray-800 text-sm">{d.teacher_name || '—'}</div>
                    <div className="text-xs text-gray-500">{d.teacher_email}</div>
                    {d.device_name && <div className="text-[10px] text-gray-400 flex items-center gap-1 mt-0.5"><Cpu size={10} />{d.device_name}</div>}
                  </td>
                  <td className="p-4 font-mono text-xs text-gray-700">{d.mac}</td>
                  <td className="p-4">
                    {d.secure_id ? (
                      <button onClick={() => copy(d.secure_id!)} title="Bấm để copy"
                        className="flex items-center gap-1.5 font-mono text-[11px] text-blue-700 hover:text-blue-900 max-w-[240px]">
                        <span className="truncate">{d.secure_id}</span>
                        <Copy size={12} className="shrink-0" />
                        {copied === d.secure_id && <span className="text-emerald-600 text-[10px] font-bold shrink-0">đã copy</span>}
                      </button>
                    ) : <span className="text-xs text-gray-400">Chỉ cấp sau khi duyệt</span>}
                  </td>
                  <td className="p-4">
                    {d.is_active
                      ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-emerald-100 text-emerald-700"><ShieldCheck size={12} /> ĐÃ DUYỆT</span>
                      : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-black bg-amber-100 text-amber-700">CHỜ DUYỆT</span>}
                  </td>
                  <td className="p-4 text-right">
                    {d.is_active ? (
                      <div className="inline-flex items-center gap-2">
                        {d.provisioned && <button onClick={() => act(d.id, 'reset-provision')} disabled={busyId === d.id}
                          className="px-3 py-1.5 rounded-lg text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 disabled:opacity-40">
                          Cấp lại khóa
                        </button>}
                        <button onClick={() => act(d.id, 'revoke')} disabled={busyId === d.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-100 disabled:opacity-40">
                          {busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <ShieldOff size={13} />} Thu hồi
                        </button>
                      </div>
                    ) : (
                      <button onClick={() => act(d.id, 'approve')} disabled={busyId === d.id}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40">
                        {busyId === d.id ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Duyệt & cấp mã
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LabDevices;
